import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useEntityPreview } from '@/contexts/EntityPreviewContext';

/**
 * EntityPreviewModal — globální read-only preview pro order/intake (UX vrstva B).
 * Render jednou v Layout, otevírá se přes context.
 */
export default function EntityPreviewModal() {
  const { preview, close } = useEntityPreview();
  const open = !!preview;
  const title = preview?.type === 'order'
    ? `Objednávka ${preview.data?.orderNumber || ''}`
    : preview?.type === 'intake'
      ? `Příjem ${preview.data?.intakeProtocolNumber || ''}`
      : '';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent
        data-testid="entity-preview-dialog"
        style={{ background: 'var(--ct-surface)', border: '1px solid var(--ct-border)', maxWidth: '640px' }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--ct-accent)', fontFamily: 'Inter' }}>
            {title}
          </DialogTitle>
        </DialogHeader>
        {preview?.type === 'order' && <OrderPreview o={preview.data} />}
        {preview?.type === 'intake' && <IntakePreview i={preview.data} />}
      </DialogContent>
    </Dialog>
  );
}

function OrderPreview({ o }) {
  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Společnost" value={o.customer?.company} />
        <Field label="Kontaktní osoba" value={o.customer?.contactPerson} />
        <Field label="E-mail" value={o.customer?.email} />
        <Field label="Telefon" value={o.customer?.phone} />
        <Field label="Stav" value={statusLabel('order', o.status)} />
        <Field label="Vytvořeno" value={o.createdAt ? new Date(o.createdAt).toLocaleString('cs-CZ') : '—'} mono />
      </div>
      {(o.instruments || []).length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--ct-text-secondary)' }}>
            Přístroje ({(o.instruments || []).length})
          </div>
          <div className="rounded text-xs overflow-hidden" style={{ border: '1px solid var(--ct-border)' }}>
            {o.instruments.map((inst, idx) => (
              <div key={idx} className="px-3 py-1.5 grid grid-cols-3 gap-2"
                style={{ borderTop: idx > 0 ? '1px solid var(--ct-border)' : 'none' }}>
                <span>{inst.name || '—'}</span>
                <span className="font-mono text-[11px]">{inst.type || '—'}</span>
                <span className="text-[11px]" style={{ color: 'var(--ct-text-secondary)' }}>{inst.serialNumber || ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {o.notes && <Field label="Poznámka" value={o.notes} />}
    </div>
  );
}

function IntakePreview({ i }) {
  return (
    <div className="space-y-3 text-sm">
      {(i.orderNumber || i.customerName) && (
        <div className="p-2 rounded grid grid-cols-2 gap-3"
          style={{ background: 'rgba(var(--ct-accent-rgb), 0.06)', border: '1px solid rgba(var(--ct-accent-rgb), 0.2)' }}>
          <Field label="Objednávka" value={i.orderNumber} mono accent />
          <Field label="Zákazník" value={i.customerName} />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Přijal" value={i.receivedByName} />
        <Field label="Datum přijetí" value={i.receivedAt ? new Date(i.receivedAt).toLocaleString('cs-CZ') : '—'} mono />
        <Field label="Odesláno ke kalibraci" value={i.sentToCalibration ? `Ano · ${i.sentToCalibrationAt ? new Date(i.sentToCalibrationAt).toLocaleDateString('cs-CZ') : ''}` : 'Ne'} />
        <Field label="Počet přístrojů" value={(i.instruments || []).length} mono />
      </div>
      {(i.instruments || []).length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--ct-text-secondary)' }}>Přístroje</div>
          <div className="rounded text-xs overflow-hidden" style={{ border: '1px solid var(--ct-border)' }}>
            {i.instruments.map((inst, idx) => (
              <div key={idx} className="px-3 py-1.5 grid grid-cols-4 gap-2"
                style={{ borderTop: idx > 0 ? '1px solid var(--ct-border)' : 'none' }}>
                <span>{inst.name || '—'}</span>
                <span className="font-mono text-[11px]">{inst.type || '—'}</span>
                <span className="font-mono text-[11px]">{inst.marking1 || ''}</span>
                <span className="font-mono text-[11px]">{inst.marking2 || ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {i.notes && <Field label="Poznámka" value={i.notes} />}
    </div>
  );
}

function Field({ label, value, mono, accent }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--ct-text-secondary)' }}>{label}</div>
      <div className={mono ? 'font-mono' : ''} style={accent ? { color: 'var(--ct-accent)' } : undefined}>
        {value || '—'}
      </div>
    </div>
  );
}

function statusLabel(kind, s) {
  if (kind === 'order') {
    return s === 'new' ? 'Nová' : s === 'confirmed' ? 'Potvrzená' : s === 'received' ? 'Přijatá' : s;
  }
  return s;
}
